import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type SortableItemProps<T> = {
  item: T;
  render: (item: T, dragProps: any) => React.ReactNode;
};

function SortableItem<T extends { id: string | number }>({
  item,
  render,
}: SortableItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={{ ...style, width: "100%" }}>
      {render(item, { listeners, attributes })}
    </div>
  );
}

type SortableListProps<T> = {
  items: T[];
  onOrderChange: (newItems: T[]) => void;
  renderItem: (item: T, dragProps: any) => React.ReactNode;
  className?: string;
};

export default function SortableList<T extends { id: string | number }>({
  items,
  onOrderChange,
  renderItem,
  className = "",
}: SortableListProps<T>) {
  const [localItems, setLocalItems] = useState(items);
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localItems.findIndex((i) => i.id === active.id);
    const newIndex = localItems.findIndex((i) => i.id === over.id);
    const newOrder = arrayMove(localItems, oldIndex, newIndex);

    setLocalItems(newOrder);
    onOrderChange(newOrder);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}>
      <SortableContext
        items={localItems.map((i) => i.id)}
        strategy={verticalListSortingStrategy}>
        <div className={`flex flex-col items-center gap-2 ${className}`}>
          {localItems.map((item) => (
            <SortableItem key={item.id} item={item} render={renderItem} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
